import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, FresnoGrizzliesLink } from "./helpers";

import StaticSeatMap from "public/images/fresnogrizzlies/GrizzliesMap.png";
import FindGamesBanner from "public/images/fresnogrizzlies/Attend-Banner_WinterDeal.jpg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Fresno-grizzlies-bear-primary.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "fresnogrizzlies_flex",
  browserTitle: "Fresno Grizzlies Fireworks Offer",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Fresno Grizzlies",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats! You have unlocked 10% off your tickets. Add 1 more game and receive 15% off, add 2 more games and get 20% off or add 3 more games for 30% off your tickets and the ability to watch Post-Game Fireworks on the field!",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        discount: 15,
        customProgressBarMessage:
          "Congrats! You have unlocked 15% off your seats! Add 1 more game and get 20% off or add 2 more games for 30% off your tickets and the ability to watch Post-Game Fireworks on the field!",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your seats! Add 1 more game for 30% off and the ability to watch Post-Game Fireworks on the field.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 3,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 40% off your seats and the ability to watchPost-Game Fireworks on the field! Add more games or checkout now.",
        customAditionalMessage:
          "  for 30% off your tickets and the ability to watch Post-Game Fireworks on the field?",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "",
    mapId: "",
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 10,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/ticketmanagement/?agency=FRGM_MYTIXX&orgid=57457#/",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at
        <Link
          href="tel:559-320-4487"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (559) 320-4487
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#d10e47",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 2 games and get 10% off, add 3 games and get 15% off, add 4 games and get 20% off or add 5 games for 30% off your tickets and unlock the ability to watch Post-Game Fireworks on the field!",
      progressBarAditionalText:
        " and get 10% off, add 2 games and get 15% off, add 3 games and get 20% off or add 4 games for 30% off your tickets and unlock the ability to watch Post-Game Fireworks on the field!",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Fresno Grizzlies tickets are confirmed! Tickets are delivered
        directly within your Fresno Grizzlies <FresnoGrizzliesLink /> Account.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your MiLB.com account to continue with seamless access.",
  },
};

export default config;
